/*
 * Copyright (c) 2021
 *    Reliant Business Valuation, LLC - http://www.reliantvalue.com
 *    Author: Kirill Vizniuk - kirill@reliantvalue.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { CurrencyPipe } from 'src/app/pipes';
import { Component, ElementRef, forwardRef, HostListener, Input, EventEmitter, ViewChild, AfterViewInit, OnChanges, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { fromEvent, noop } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';

export const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => NumberInputComponent),
  multi: true
};
@Component({
  selector: 'number-input',
  templateUrl: './number-input.component.html',
  styleUrls: ['./number-input.component.scss'],
  providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR]
})

export class NumberInputComponent implements ControlValueAccessor, AfterViewInit, OnChanges {
  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (_: any) => void = noop;

  @ViewChild('numberInput') numberInput: ElementRef;

  @Input() name: string = '';
  @Input() precision: number = 0;
  @Input() required: boolean = false;
  @Input() id: string = this.name;
  @Input() disabled: boolean = false;
  @Input() placeholder: string = '0';
  @Input() max: number = null;
  @Input() maxlength: number = 8;
  @Output() blur = new EventEmitter();
  @Output() minPointTouched = new EventEmitter();
  @Input() min: number = null;
  @Input() pasteDisabled: boolean = false;
  @Input() displayValue: string = '';
  @Input() readonly: boolean = false;
  @Input() classes: string = '';
  @Input() allowNegative: boolean = false;
  @Input() clearZero: boolean = true;
  @Input() allowZero: boolean = false;
  @Output() outValue = new EventEmitter<string>(null);
  @Input() needPercentFormatter: boolean = false;

  numberValue: number;
  Align = Align;

  constructor(
    private elementRef: ElementRef,
    private currencyPipe: CurrencyPipe) {
    const eventStream = fromEvent(elementRef.nativeElement, 'keyup')
      .pipe(
        map(() => ''),
        debounceTime(300),
        // distinctUntilChanged()
      );

    eventStream.subscribe((input) => {
      this.outValue.emit(input);
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => { this.formatNumber(); }, 100);
  }

  ngOnChanges(): void {
    this.formatNumber();
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  writeValue(obj: number): void {
    if (obj != null && typeof obj != 'undefined') {
      this.displayValue = String(obj);
      this.numberValue = obj;
      this.formatNumber();
    } else if (obj === null || typeof obj === 'undefined') {
      this.displayValue = '';
      this.numberValue = undefined;
    }
  }

  /** On Focus remove 0 if default */
  onFocus(): void {
    if ((this.displayValue == '0' || this.displayValue == '0.00') && this.clearZero) {
      this.displayValue = '';
      this.numberValue = undefined;
      // Write value in ngModel
      this.onChangeCallback(this.numberValue);
    }
  }

  /** Remove . if only and focus lost */
  onFocusOut(): void {
    // If user only enters '-' value and he lefts the input, make it blank as its invalid number
    if (this.displayValue === '-') {
      this.displayValue = '';
    }

    // if first digit is '0' then remove that
    if (this.displayValue.length > 1 && this.displayValue[0] == '0') {
      this.displayValue = this.displayValue.replace(/^0+/, '');
      this.formatNumber();
    }

    if (this.displayValue.indexOf('.') != -1 && this.displayValue.substr(this.displayValue.indexOf('.') + 1, this.displayValue.length) == '')
      this.displayValue = this.displayValue.substr(0, this.displayValue.length - 1);
  }

  onPaste(event: any): void {
    if (!this.pasteDisabled) {
      let data;
      let selStart = event.target.selectionStart;
      let selEnd = event.target.selectionEnd;
      let newRawValue = ''; // New Value
      if ((<any>window).clipboardData && (<any>window).clipboardData.getData) // IE
        data = (<any>window).clipboardData.getData('Text');
      else
        data = (event.originalEvent || event).clipboardData.getData('text/plain');

      let pastedData = data.replace(/[^0-9.]/gm, '');

      let parsed;
      if (pastedData) {
        if (selStart == selEnd)
          newRawValue = this.displayValue.slice(0, selStart) + pastedData + this.displayValue.slice(selStart);
        else
          newRawValue = this.displayValue.slice(0, selStart) + pastedData + this.displayValue.slice(selEnd);

        // If length is maximum then shorten the value
        if (newRawValue.length > this.maxlength ||
          (newRawValue.indexOf('.') != -1 && newRawValue.length < (this.maxlength + this.precision + 1))) {
          if (newRawValue.indexOf('.') > -1)
            newRawValue = newRawValue.substr(0, (this.maxlength + this.precision + 1));
          else
            newRawValue = newRawValue.substr(0, this.maxlength);
        }

        // Parse the new obtained value (Used in ngModel)
        parsed = this.currencyPipe.parse(newRawValue, this.precision);

        // Transform the new value (Used for Display purpose)
        let transformed = this.currencyPipe.transform(parsed, this.precision);

        // Set transformed value into display
        this.displayValue = transformed;
      }

      // Set value of the number in ngModel
      this.numberValue = parsed;

      // Set the position of the cursor after removing number
      // setTimeout(()=>{
      //   // If any comma is removed from the number then move cursor to ahead of two places
      //   if(removingComma || (willCommaRemove && !removingDecimal))
      //     event.target.setSelectionRange(selStart-2, selStart-2);
      //   else
      //     event.target.setSelectionRange(selStart-1, selStart-1);
      // }, 0);

      if (this.checkMinNumber())
        this.formatMinNumber();

      if (this.checkMaxNumber())
        this.formatMaxNumber();

      // Write value in ngModel
      this.onChangeCallback(this.numberValue);
    }

    event.preventDefault();
  }

  /**
   * This listener is used for deleting value
   * */
  @HostListener('keydown', ['$event'])
  handleKeydown(event: any): void {
    let keyCode = Number(event.which || event.charCode || event.keyCode);
    if (keyCode == 8 || keyCode == 46 || keyCode == 63272) {

      // Handle Backspace
      if (keyCode === 8) {
        // If we are removing only single character
        if (event.target.selectionStart === event.target.selectionEnd) {
          let selStart = event.target.selectionStart; // Position of Cursor
          let newRawValue = ''; // New Value
          let removingComma = false; // Check cursor is pointing to separator(',') ?
          let removingDecimal = (this.displayValue.indexOf('.') != -1 && selStart > this.displayValue.indexOf('.') + 1);
          let removeDecimal = (this.displayValue.indexOf('.') != -1 && selStart == this.displayValue.indexOf('.') + 1);
          // Check after removing number any separator will be removed or not, if true set position of cursor accordingly
          // let willCommaRemove = (this.displayValue.replace(new RegExp(',', 'g'), '')).length % 3 === 1;
          let willCommaRemove = this.willCommaRemove;

          // Store new value into 'newRawValue' after removing number from the cursor position
          if (this.displayValue.length > 1) {
            removingComma = this.displayValue.slice(selStart - 1, selStart) === ',';

            // If cursor is pointing to comma then remove previous number before separator
            if (removeDecimal)
              newRawValue = this.displayValue.slice(0, selStart - 1);
            else if (removingComma)
              newRawValue = this.displayValue.slice(0, selStart - 2) + this.displayValue.slice(selStart, this.displayValue.length);
            else if (selStart > 0)
              newRawValue = this.displayValue.slice(0, selStart - 1) + this.displayValue.slice(selStart, this.displayValue.length);
            else if (selStart == 0)
              newRawValue = this.displayValue;
          }

          let parsed;
          // Parse the new obtained value (Used in ngModel)
          if (newRawValue) {
            parsed = this.currencyPipe.parse(newRawValue, this.precision);

            // Transform the new value (Used for Display purpose)
            let transformed = this.currencyPipe.transform(parsed, this.precision);

            // Set transformed value into display
            this.displayValue = transformed;

            /* If number is decimal and after parsing if it becomes integer then add '.00' in display value
              Ex. Previous number = 1234.00
              After Removing = 123, Then add .00 After the number */
            if (newRawValue.indexOf('.') != -1 && this.displayValue.indexOf('.') == -1
              && newRawValue.substr(newRawValue.indexOf('.') + 1, newRawValue.length) != '') {
              this.displayValue += '.';
            }
          } else {
            this.displayValue = '';
          }

          // Set value of the number in ngModel
          this.numberValue = parsed;

          // Set the position of the cursor after removing number
          setTimeout(() => {
            // If any comma is removed from the number then move cursor to ahead of two places
            if (removingComma || (willCommaRemove && !removingDecimal && !removeDecimal))
              event.target.setSelectionRange(selStart - 2, selStart - 2);
            else
              event.target.setSelectionRange(selStart - 1, selStart - 1);
          }, 0);

          // Check min number
          if (this.checkMinNumber())
            this.formatMinNumber();

          // Write value in ngModel
          this.onChangeCallback(this.numberValue);

          // Prevent original event
          event.preventDefault();
        }
        // Backspace With Selected multiple character
        else {
          let selStart = event.target.selectionStart; // Starting position of Cursor
          let selEnd = event.target.selectionEnd; // End position of cursor
          let newRawValue = ''; // Variable to store new value

          // Set new value without selected range
          newRawValue = this.displayValue.slice(0, selStart) + this.displayValue.slice(selEnd, this.displayValue.length);

          let parsed;
          if (newRawValue) {
            // Parse the new obtained value (Used in ngModel)
            parsed = this.currencyPipe.parse(newRawValue, this.precision);

            // Transform the new value (Used for Display purpose)
            let transformed = this.currencyPipe.transform(parsed, this.precision);

            // Set transformed value into display
            this.displayValue = transformed;

            // If number is decimal and after parsing if it becomes integer then add '.00' in display value
            // Ex. Previous number = 1234.00
            // After Removing = 123, Then add .00 After the number
            if (newRawValue.indexOf('.') != -1 && this.displayValue.indexOf('.') == -1
              && newRawValue.substr(newRawValue.indexOf('.') + 1, newRawValue.length) != '') {
              this.displayValue += '.';
            }
          } else {
            this.displayValue = '';
          }

          // Set value of the number in ngModel
          this.numberValue = parsed;

          // Set the position of the cursor after removing number
          setTimeout(() => {
            event.target.setSelectionRange(selStart, selStart);
          }, 0);

          // Check min number
          if (this.checkMinNumber())
            this.formatMinNumber();

          // Write value in ngModel
          this.onChangeCallback(this.numberValue);

          // Prevent original event
          event.preventDefault();
        }
      }
      // Handle Delete
      else if (keyCode === 46) {
        // If we are deleting only single character
        if (event.target.selectionStart === event.target.selectionEnd) {
          let selStart = event.target.selectionStart; // Position of Cursor
          let newRawValue = ''; // New Value
          let removingComma = false; // Check cursor is pointing to separator(',') ?
          let removeDecimal = (this.displayValue.indexOf('.') != -1 && selStart == this.displayValue.indexOf('.'));

          // Check after deleting number any separator will be removed or not, if true set position of cursor accordingly
          // let willCommaRemove = (this.displayValue.replace(new RegExp(',', 'g'), '')).length % 3 === 1;
          let willCommaRemove = this.willCommaRemove;

          // Store new value into 'newRawValue' after deleting number from the cursor position
          if (this.displayValue.length > 1) {
            removingComma = this.displayValue.slice(selStart, selStart + 1) === ',';
            if (removeDecimal)
              newRawValue = this.displayValue.slice(0, selStart);
            else if (removingComma)
              newRawValue = this.displayValue.slice(0, selStart) + this.displayValue.slice(selStart + 2, this.displayValue.length);
            else
              newRawValue = this.displayValue.slice(0, selStart) + this.displayValue.slice(selStart + 1, this.displayValue.length);
          }

          let parsed;
          if (newRawValue) {
            // Parse the new obtained value (Used in ngModel)
            parsed = this.currencyPipe.parse(newRawValue, this.precision);

            // Transform the new value (Used for Display purpose)
            let transformed = this.currencyPipe.transform(parsed, this.precision);

            // Set transformed value into display
            this.displayValue = transformed;

            /* If number is decimal and after parsing if it becomes integer then add '.00' in display value
              Ex. Previous number = 1234.00
              After Removing = 123, Then add .00 After the number */
            if (newRawValue.indexOf('.') != -1 && this.displayValue.indexOf('.') == -1
              && newRawValue.substr(newRawValue.indexOf('.') + 1, newRawValue.length) != '') {
              this.displayValue += '.';
            }
          } else {
            this.displayValue = '';
          }

          // Set value of the number in ngModel
          this.numberValue = parsed;

          // Set the position of the cursor after removing number
          setTimeout(() => {
            if (removingComma) {
              if (willCommaRemove)
                event.target.setSelectionRange(selStart, selStart);
              else
                event.target.setSelectionRange(selStart + 1, selStart + 1);
            }
            else {
              event.target.setSelectionRange(selStart, selStart);
            }
          }, 0);

          // Check min number
          if (this.checkMinNumber())
            this.formatMinNumber();

          // Write value in ngModel
          this.onChangeCallback(this.numberValue);

          // Prevent original event
          event.preventDefault();
        }
        // Delete With Selected multiple character
        else {
          let selStart = event.target.selectionStart; // Starting Position of Cursor
          let selEnd = event.target.selectionEnd; // Ending Position of Cursor
          let newRawValue = ''; // New Value

          // Store new value into 'newRawValue' after deleting number from the selected range
          newRawValue = this.displayValue.slice(0, selStart) + this.displayValue.slice(selEnd, this.displayValue.length);

          let parsed;
          if (newRawValue) {
            // Parse the new obtained value (Used in ngModel)
            parsed = this.currencyPipe.parse(newRawValue, this.precision);

            // Transform the new value (Used for Display purpose)
            let transformed = this.currencyPipe.transform(parsed, this.precision);

            // Set transformed value into display
            this.displayValue = transformed;

            /* If number is decimal and after parsing if it becomes integer then add '.00' in display value
              Ex. Previous number = 1234.00
              After Removing = 123, Then add .00 After the number */
            if (newRawValue.indexOf('.') != -1 && this.displayValue.indexOf('.') == -1
              && newRawValue.substr(newRawValue.indexOf('.') + 1, newRawValue.length) != '') {
              this.displayValue += '.';
            }
          } else {
            this.displayValue = '';
          }

          // Set value of the number in ngModel
          this.numberValue = parsed;

          // Set the position of the cursor after removing number
          setTimeout(() => {
            event.target.setSelectionRange(selStart, selStart);
          }, 0);

          // Check min number
          if (this.checkMinNumber())
            this.formatMinNumber();

          // Write value in ngModel
          this.onChangeCallback(this.numberValue);

          // Prevent original event
          event.preventDefault();
        }
      }
    }
  }

  @HostListener('keypress', ['$event'])
  handleKeypress(event: any): void {
    let keyCode = Number(event.which || event.charCode || event.keyCode);
    let keyChar = String.fromCharCode(keyCode);
    event.preventDefault();

    let selStart = event.target.selectionStart;
    let selEnd = event.target.selectionEnd;

    /**
     * This condition means if key pressed value is from 0 to 9
     * */
    if (keyCode >= 48 && keyCode <= 58) {
      /**
       * Remove all comma's (,) from current value
       * */
      let checkValueLength = this.displayValue.replace(/,/g, '');

      /**
       * This condition will only pass if total length of the value is in range of maxlength
       * First condition means if total length of the value is less than maxlength
       *        (ex. maxlength = 10 and current value is 10.22 so its valid)
       * Second condition means current value contains decimal and it is less than maxlength + total precision
       *        (ex. maxlength = 10 and current value is '123456789.12' this means its valid because we are not calculating precision + decimal(.) in mxx length)
       * Third condition is current value contains decimal and cursor position is after decimal point
       *        (ex. current value is 10.25 and cursor is pointing after 10.)
       * */
      if (checkValueLength.length < this.maxlength ||
        (this.displayValue.indexOf('.') != -1 && checkValueLength.length < (this.maxlength + this.precision + 1)) ||
        (this.displayValue.indexOf('.') != -1 && selStart > this.displayValue.indexOf('.'))) {

        /**
         * This condition means current value not contains any decimal value or cursor is pointing before decimal place
         * */
        if (this.displayValue.indexOf('.') == -1 || selStart <= this.displayValue.indexOf('.')) {

          let newRawValue = '';
          if (selStart === selEnd)
            newRawValue = this.displayValue.slice(0, selStart) + keyChar + this.displayValue.slice(selStart);
          else
            newRawValue = this.displayValue.slice(0, selStart) + keyChar + this.displayValue.slice(selEnd);

          let parsed = this.currencyPipe.parse(newRawValue, this.precision);
          let transformed = this.currencyPipe.transform(parsed, this.precision);
          let willCommaAdded = this.willCommaAdded;

          this.displayValue = transformed;
          if (newRawValue.indexOf('.') != -1 && this.displayValue.indexOf('.') == -1
            && newRawValue.substr(newRawValue.indexOf('.') + 1, newRawValue.length) != '') {
            this.displayValue += '.';
          }

          this.numberValue = Number(parsed);

          if (this.checkMinNumber())
            this.formatMinNumber();

          if (this.checkMaxNumber())
            this.formatMaxNumber();

          if (willCommaAdded) {
            selStart += 2;
          } else {
            selStart += 1;
          }
        }
        /**
         * This means current value is decimal and cursor is pointing after decimal place
         * */
        else {
          let newRawValue = '';

          /**
           * This condition means total decimal places of current value are less than specified decimal places
           * */
          if (this.displayValue.substr(this.displayValue.indexOf('.') + 1, this.displayValue.length).length <= this.precision) {

            if (selStart === selEnd)
              newRawValue = this.displayValue.slice(0, selStart) + keyChar + this.displayValue.slice(selStart);
            else
              newRawValue = this.displayValue.slice(0, selStart) + keyChar + this.displayValue.slice(selEnd);

            if (newRawValue.substr(newRawValue.indexOf('.') + 1, newRawValue.length).length > this.precision) {
              newRawValue = newRawValue.substr(0, newRawValue.indexOf('.') + (this.precision + 1));
              selStart += 1;
            }

            let parsed = this.currencyPipe.parse(newRawValue, this.precision);
            let transformed = this.currencyPipe.transform(parsed, this.precision);

            this.displayValue = transformed;
            if (newRawValue.indexOf('.') != -1 && this.displayValue.indexOf('.') == -1
              && newRawValue.substr(newRawValue.indexOf('.') + 1, newRawValue.length) != '') {
              this.displayValue += '.';
            }

            this.numberValue = Number(parsed);

            if (this.checkMinNumber())
              this.formatMinNumber();

            if (this.checkMaxNumber())
              this.formatMaxNumber();

            // if(selStart == this.displayValue.length || this.displayValue.length - selStart == this.precision)
            selStart++;

            // }
          }
        }
      }
    }
    /** This means user pressed (-) Negative sign */
    else if (keyCode === 45 && this.allowNegative) {
      if (selStart === 0 && this.displayValue.indexOf('-') === -1) {
        let newRawValue = keyChar + this.displayValue;
        let parsed = this.currencyPipe.parse(newRawValue, this.precision);
        let transformed = this.currencyPipe.transform(parsed, this.precision);
        this.displayValue = transformed;
        if (!isNaN(Number(parsed)))
          this.numberValue = Number(parsed);

        if (this.checkMinNumber())
          this.formatMinNumber();

        if (this.checkMaxNumber())
          this.formatMaxNumber();

        selStart++;
      }
    }
    /**
     * This means user pressed (.) Decimal Separator
     * */
    else if (keyCode == 46) { // Decimal Separator (.) pressed
      if (this.precision > 0) {
        if (this.displayValue.indexOf('.') == -1) {
          this.displayValue += '.';
          selStart++;
        }
      }
    }
    /**
     * Other Unhandled Keys
     * */
    else {
      // Other Unhandled Keys
    }

    setTimeout(() => {
      event.target.setSelectionRange(selStart, selStart);
    }, 0);

    this.onChangeCallback(this.numberValue);
  }

  // Check input number is greater than max range or not
  checkMinNumber(): boolean {
    if (this.min && this.numberValue < this.min)
      return true;
    else
      return false;
  }

  // Format min number
  formatMinNumber(): void {
    let oldDisplayValue = this.displayValue;
    let newRawValue = String(this.min);
    this.numberValue = this.min;
    let transformed = this.currencyPipe.transform(newRawValue, this.precision);
    this.displayValue = transformed;

    if (oldDisplayValue.indexOf('.') != -1) {
      this.displayValue += '.';
    }

    // Write value in ngModel
    this.onChangeCallback(this.numberValue);

    this.minPointTouched.emit();
  }

  // Check input number is greater than max range or not
  checkMaxNumber(): boolean {
    if (this.max && this.numberValue > this.max)
      return true;
    else
      return false;
  }

  // Format max number
  formatMaxNumber(): void {
    let oldDisplayValue = this.displayValue;
    let newRawValue = String(this.max);
    this.numberValue = this.max;
    let transformed = this.currencyPipe.transform(newRawValue, this.precision);
    this.displayValue = transformed;

    if (oldDisplayValue.indexOf('.') != -1) {
      this.displayValue += '.';
    }

    // Write value in ngModel
    this.onChangeCallback(this.numberValue);
  }

  // Format the number
  formatNumber(): void {
    if (this.displayValue) {
      // Parse the new obtained value (Used in ngModel)
      let parsed = this.currencyPipe.parse(this.displayValue, this.precision);

      // Transform the new value (Used for Display purpose)
      let transformed = this.currencyPipe.transform(parsed, this.precision);

      // Set transformed value into display
      this.displayValue = transformed;

      // If the value of the decimal number is 123.1 or 123.2 etc then add one more '0' to make number fixed with two decimal
      if (this.displayValue.indexOf('.') != -1) {
        // blank
      }

      if (this.checkMinNumber())
        this.formatMinNumber();

      if (this.checkMaxNumber())
        this.formatMaxNumber();

      // Write value in ngModel
      this.onChangeCallback(this.numberValue);
    }
  }

  /** Blur event */
  onBlur(): void {
    if (!this.allowZero && this.displayValue == "0") {
      this.displayValue = "";
      this.numberValue = null;
      this.onChangeCallback(null);
    }
    this.blur.emit();
  }

  /** Check comma will be removed for negative number */
  get willCommaRemove(): boolean {
    let willCommaRemove = false;
    let isNegativeNumber = this.displayValue.indexOf('-') > -1;
    if (isNegativeNumber) {
      let calcLength = (this.displayValue.replace(new RegExp(',', 'g'), '')).length;
      if (calcLength > 2) {
        willCommaRemove = (calcLength - 1) % 3 === 1;
      }
    } else {
      willCommaRemove = (this.displayValue.replace(new RegExp(',', 'g'), '')).length % 3 === 1;
    }

    return willCommaRemove;
  }

  /** Check will comma added */
  get willCommaAdded(): boolean {
    let willCommaAdded = false;
    let isNegativeNumber = this.displayValue.indexOf('-') > -1;
    if (isNegativeNumber) {
      let calcLength = (this.displayValue.replace(new RegExp(',', 'g'), '')).length;
      if (calcLength > 2) {
        willCommaAdded = (calcLength - 1) % 3 === 0;
      }
    } else {
      willCommaAdded = (this.displayValue.replace(new RegExp(',', 'g'), '')).length % 3 === 0;
    }
    return willCommaAdded;
  }

}

export enum Align {
  LEFT = 'LEFT',
  RIGHT = 'RIGHT'
}
